import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

interface RevealProps {
    children: ReactNode
    className?: string
    delay?: number
}

/**
 * Fades and lifts its content in the first time it scrolls into view.
 * Content stays visible without JavaScript and with reduced motion (see globals.css).
 */
export const Reveal: FC<RevealProps> = ({ children, className, delay = 0 }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [shown, setShown] = useState(false)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        if (!('IntersectionObserver' in window)) {
            setShown(true)
            return
        }

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                setShown(true)
                observer.disconnect()
            }
        }, { rootMargin: '0px 0px -8% 0px' })

        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    return (
        <div
            ref={ref}
            data-reveal={shown ? 'shown' : 'hidden'}
            className={className}
            style={delay ? { transitionDelay: `${delay}ms` } : undefined}
        >
            {children}
        </div>
    )
}
